<template>
  <div class="strip-chart-container">
    <!-- Filters Section -->
    <vx-card>
      <div class="p-4">
        <h4 class="mb-4">Strip Chart Filters</h4>
        
        <vs-row>
          <!-- Item Sub Group Filter -->
          <vs-col vs-xs="12" vs-sm="6" vs-lg="3" class="mb-3">
            <label class="mb-2">Item Sub Group <span class="text-danger">*</span></label>
            <SelectForeign
              :model.sync="filters.itemSubGroup"
              placeholder="Select Item Sub Group"
              :multiple="false"
              :load_initial="true"
              :label_keys="['description', 'code']"
              label_separator="/"
              :show_id_in_label="false"
              :endpoint="itemSubGroupEndpoint"
              container_class="w-full"
              input_class=""
              :use_id_as_value="true"
              :close_on_select="true"
              @input="onFilterChange"
            />
          </vs-col>

          <!-- Side Filter -->
          <vs-col vs-xs="12" vs-sm="6" vs-lg="3" class="mb-3">
            <label class="mb-2">Side <span class="text-danger">*</span></label>
            <SelectForeign
              ref="sideSelect"
              :model.sync="filters.side"
              placeholder="Select Side"
              :multiple="true"
              :load_initial="true"
              :label_keys="['description', 'code']"
              label_separator="/"
              :show_id_in_label="false"
              :endpoint="sideEndpoint"
              container_class="w-full"
              input_class=""
              :use_id_as_value="true"
              :close_on_select="false"
              @input="onFilterChange"
            />
          </vs-col>

          <!-- Lane Filter -->
          <vs-col vs-xs="12" vs-sm="6" vs-lg="3" class="mb-3">
            <label class="mb-2">Lane <span class="text-danger">*</span></label>
            <SelectForeign
              ref="laneSelect"
              :model.sync="filters.lane"
              placeholder="Select Lane"
              :multiple="true"
              :load_initial="true"
              :label_keys="['description', 'code']"
              label_separator="/"
              :show_id_in_label="false"
              :endpoint="laneEndpoint"
              container_class="w-full"
              input_class=""
              :use_id_as_value="true"
              :close_on_select="false"
              @input="onFilterChange"
            />
          </vs-col>

          <!-- Financial (FY) Filter -->
          <vs-col vs-xs="12" vs-sm="6" vs-lg="3" class="mb-3">
            <label class="mb-2">Financial Year <span class="text-danger">*</span></label>
            <SelectForeign
              ref="financialSelect"
              :model.sync="filters.financial"
              placeholder="Select Financial Year"
              :multiple="true"
              :load_initial="true"
              :label_keys="['fy_year', 'fy']"
              :show_id_in_label="false"
              :endpoint="financialYearEndpoint"
              container_class="w-full"
              input_class=""
              :use_id_as_value="true"
              :close_on_select="false"
              @input="onFilterChange"
            />
          </vs-col>
        </vs-row>

        <vs-row>
          <!-- From Chain Filter -->
          <vs-col vs-xs="12" vs-sm="6" vs-lg="3" class="mb-3">
            <label class="mb-2">From Chain <span class="text-danger">*</span></label>
            <vs-input 
              v-model="filters.fromChain"
              type="number"
              placeholder="Enter From Chain"
              class="w-full"
              @input="onFilterChange"
            />
          </vs-col>

          <!-- Difference/Duration Input -->
          <vs-col vs-xs="12" vs-sm="6" vs-lg="3" class="mb-3">
            <label class="mb-2">Chainage intervel (KM) <span class="text-danger">*</span></label>
            <vs-input 
              v-model="filters.difference"
              type="number"
              placeholder="Enter Chainage intervel (KM)"
              class="w-full"
              @input="onFilterChange"
            />
          </vs-col>

          <!-- To Chain Filter -->
          <vs-col vs-xs="12" vs-sm="6" vs-lg="3" class="mb-3">
            <label class="mb-2">To Chain <span class="text-danger">*</span></label>
            <vs-input 
              v-model="filters.toChain"
              type="number"
              placeholder="Enter To Chain"
              class="w-full"
              @input="onFilterChange"
            />
          </vs-col>

          <!-- Action Buttons -->
          <vs-col vs-xs="12" vs-sm="6" vs-lg="3" class="mb-3 flex items-end">
            <div class="w-full flex gap-2">
              <vs-button 
                color="primary"
                class="flex-1"
                @click="applyFilters"
                :disabled="!isFilterValid || loading"
              >
                <i class="feather icon-filter"></i>
                Apply
              </vs-button>
              <vs-button 
                color="warning"
                type="border"
                class="flex-1"
                @click="resetFilters"
                :disabled="loading"
              >
                <i class="feather icon-rotate-ccw"></i>
                Reset
              </vs-button>
            </div>
          </vs-col>
        </vs-row>
      </div>
    </vx-card>

    <!-- Chart Section -->
    <vx-card class="mt-4" v-if="loading || processedData.length > 0 || isFilterValid">
      <div class="p-4">
        <div class="flex items-center justify-between mb-2">
          <h4>Strip Chart</h4>
          <div v-if="processedData.length > 0 && !loading" class="flex gap-2">
            <vs-button 
                color="success" 
                type="filled" 
                size="small"
                @click="exportExcel"
            >
                <i class="feather icon-download"></i>
                Export Excel
            </vs-button>
          </div>
        </div>

        

        <div v-if="loading" class="text-center p-8">
          <vs-progress indeterminate color="primary"></vs-progress>
          <p class="mt-4">Fetching data and generating chart...</p>
        </div>

        <div v-else-if="processedData.length > 0" class="strip-chart-wrapper">
          <div class="table-responsive">
            <table class="strip-chart-table">
              <thead>
                <tr class="header-row">
                  <th class="sticky-col side-head">Side</th>
                  <th class="sticky-col fy-lane-head">FY / Lane</th>
                  <th v-for="point in chainagePoints.slice(0, -1)" :key="point" class="grid-col-head">
                    <div class="col-highlight-helper"></div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <template v-for="(sideGroup, sIdx) in processedData">
                  <!-- Side Data Rows -->
                  <template v-for="(fyGroup, fIdx) in sideGroup.fys">
                    <tr v-for="(laneGroup, lIdx) in fyGroup.lanes" :key="`row-${sIdx}-${fIdx}-${lIdx}`" class="data-row">
                      <!-- Side Column (Merged) -->
                      <td v-if="fIdx === 0 && lIdx === 0" 
                          :rowspan="getRowSpan(sideGroup)" 
                          class="sticky-col side-cell">
                        <div class="side-badge">{{ sideGroup.sideLabel }}</div>
                      </td>
                      
                      <!-- Combined FY & Lane Column -->
                      <td class="sticky-col fy-lane-cell">
                        <div class="fy-info">
                          <span class="fy-label">{{ fyGroup.fyYear }}</span>
                          <span class="lane-label">{{ laneGroup.laneLabel }}</span>
                        </div>
                      </td>

                      <!-- Data Cells -->
                      <td v-for="(point, pIdx) in chainagePoints.slice(0, -1)" 
                          :key="`cell-${sIdx}-${fIdx}-${lIdx}-${pIdx}`" 
                          class="grid-cell"
                          :class="getCellClass(laneGroup.transactions, point, chainagePoints[pIdx+1])"
                          :style="getCellStyle(fyGroup.fyYear, fyGroup.fyKey, laneGroup.transactions, point, chainagePoints[pIdx+1])"
                          :title="getCellTooltip(laneGroup.transactions, point, chainagePoints[pIdx+1])">
                        <div class="cell-content" v-if="isCellActive(laneGroup.transactions, point, chainagePoints[pIdx+1])">
                        </div>
                      </td>
                    </tr>
                  </template>
                  
                  <!-- Chainage Row after each Side group -->
                  <tr :key="`chainage-${sIdx}`" class="chainage-row">
                    <td class="sticky-col side-cell empty-cell"></td>
                    <td class="sticky-col fy-lane-cell title-cell">Chainage</td>
                    <td v-for="(point, pointIdx) in chainagePoints.slice(0, -1)" :key="`point-${sIdx}-${point}`" class="chainage-label-cell" :class="{ 'first-point-cell': pointIdx === 0 }">
                      <div class="chainage-tick"></div>
                      <div class="chainage-label" :title="String(point)">{{ formatChainageLabel(point) }}</div>
                    </td>
                    <!-- End Point Label -->
                    <td class="chainage-label-cell last-point-cell">
                      <div class="chainage-tick end-tick"></div>
                      <div class="chainage-label" :title="String(chainagePoints[chainagePoints.length - 1])">{{ formatChainageLabel(chainagePoints[chainagePoints.length - 1]) }}</div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>

        <div v-else class="chart-placeholder">
          <p class="text-center text-gray-500">
            {{ getEmptyStateMessage() }}
          </p>
        </div>
      </div>
    </vx-card>
  </div>
</template>

<script>
import SelectForeign from '@/components/letstream/ls-select-foreign/SelectForeign.vue'
import { Request } from '@/utils/requests'
import moment from 'moment'

export default {
  name: 'StripChart',
  components: {
    SelectForeign
  },
  data() {
    return {
      filters: {
        itemSubGroup: null,
        side: [],
        lane: [],
        financial: [],
        fromChain: '',
        difference: '',
        toChain: ''
      },
      loading: false,
      activeFilters: {},
      selectedSideLabelMap: {},
      selectedLaneLabelMap: {},
      selectedFinancialYearLabelMap: {},
      rawResponse: null,
      processedData: [],
      hasAppliedFilters: false
    }
  },
  computed: {
    orgId() {
      return this.$store.state.organisation.organisation && this.$store.state.organisation.organisation.id
    },
    spvName() {
      const org = this.$store.state.organisation.organisation
      return (org && (org.name || org.organisation_name || org.spv_name)) || ''
    },
    itemSubGroupEndpoint() {
      return this.orgId ? `${this.$store.state.apiendpoints.master_item_sub_group}?organisation=${this.orgId}&is_active=true` : this.$store.state.apiendpoints.master_item_sub_group
    },
    sideEndpoint() {
      return this.orgId ? `${this.$store.state.apiendpoints.master_side}?organisation=${this.orgId}&is_active=true` : `${this.$store.state.apiendpoints.master_side}?is_active=true`
    },
    laneEndpoint() {
      return this.orgId ? `${this.$store.state.apiendpoints.master_lane}?organisation=${this.orgId}&is_active=true` : `${this.$store.state.apiendpoints.master_lane}?is_active=true`
    },
    financialYearEndpoint() {
      return this.orgId ? `${this.$store.state.apiendpoints.master_year}?organisation=${this.orgId}&is_active=true` : `${this.$store.state.apiendpoints.master_year}?is_active=true`
    },
    isFilterValid() {
      return this.filters.itemSubGroup && 
             (this.filters.side && this.filters.side.length > 0) && 
             (this.filters.lane && this.filters.lane.length > 0) && 
             (this.filters.financial && this.filters.financial.length > 0) &&
             this.filters.fromChain !== '' && 
             this.filters.toChain !== '' && 
             this.filters.difference !== ''
    },
    subGroupLabels() {
      let data = this.extractDataArray(this.rawResponse)
      if (data.length === 0) return []
      const labels = data.map(item => this.getSubGroupLabel(item)).filter(Boolean)
      return [...new Set(labels)]
    },
    chainagePoints() {
      if (!this.activeFilters.fromChain || !this.activeFilters.toChain || !this.activeFilters.difference) return []
      
      const start = parseFloat(this.activeFilters.fromChain)
      const end = parseFloat(this.activeFilters.toChain)
      const step = parseFloat(this.activeFilters.difference)
      
      if (isNaN(start) || isNaN(end) || isNaN(step) || step <= 0) return []
      
      const points = []
      // Use low to high comparison always for grid generation
      const s = Math.min(start, end)
      const e = Math.max(start, end)
      const maxPoints = 1500
      
      for (let val = s; val <= e && points.length < maxPoints; val += step) {
        points.push(parseFloat(val.toFixed(3)))
      }
      
      const lastPoint = points[points.length - 1] || s
      if (lastPoint < e) {
        points.push(parseFloat(e.toFixed(3)))
      }
      
      return points
    },
    yearColorMap() {
      const palette = ['#5CC24A', '#1E88E5', '#FB8C00', '#8E24AA', '#00897B', '#E53935', '#6D4C41', '#546E7A']
      const years = []
      
      this.processedData.forEach(sideGroup => {
        sideGroup.fys.forEach(fyGroup => {
          const yearStr = String(fyGroup.fyYear)
          if (!years.includes(yearStr)) years.push(yearStr)
        })
      })
      
      const map = {}
      years.sort().forEach((yearStr, idx) => {
        map[yearStr] = palette[idx % palette.length]
      })
      return map
    }
  },
  methods: {
    extractDataArray(res) {
      if (!res) return []
      if (Array.isArray(res)) return res
      if (res.results && Array.isArray(res.results)) return res.results
      if (res.data && Array.isArray(res.data)) return res.data
      if (res.data && res.data.data && Array.isArray(res.data.data)) return res.data.data
      if (res.data && res.data.results && Array.isArray(res.data.results)) return res.data.results
      return []
    },
    getDisplayLabel(entity, preferredKeys = []) {
      if (!entity || typeof entity !== 'object') return ''
      const fallbackKeys = ['name', 'description', 'label', 'code']
      const keys = [...preferredKeys, ...fallbackKeys]
      for (const key of keys) {
        if (entity[key]) return entity[key]
      }
      return ''
    },
    getSubGroupLabel(item) {
      if (!item) return ''
      if (item.item_sub_group_detail && typeof item.item_sub_group_detail === 'object') {
        return this.getDisplayLabel(item.item_sub_group_detail, ['description', 'name', 'code']) || item.item_sub_group_code || ''
      }
      if (item.item_sub_group && typeof item.item_sub_group === 'object') {
        return this.getDisplayLabel(item.item_sub_group, ['description', 'name', 'code']) || item.item_sub_group_code || ''
      }
      return item.item_sub_group_name || item.item_sub_group_description || item.item_sub_group_code || ''
    },
    formatChainageLabel(value) {
      const num = Number(value)
      if (!Number.isFinite(num)) return '-'

      const abs = Math.abs(num)
      if (abs >= 1000000) {
        return num.toExponential(2).replace('+', '')
      }
      if (abs >= 1000) {
        return num.toLocaleString('en-IN', { maximumFractionDigits: 2 })
      }

      return parseFloat(num.toFixed(3)).toString()
    },
    formatTransactionDate(dateValue) {
      if (!dateValue) return 'N/A'
      const parsed = moment(dateValue)
      return parsed.isValid() ? parsed.format('DD-MM-YYYY') : dateValue
    },
    getEmptyStateMessage() {
      if (!this.isFilterValid) return 'Please select all mandatory filters'
      if (this.hasAppliedFilters) return 'No strip chart data found for selected filters'
      return 'Click Apply to generate strip chart'
    },
    getArrayValue(source, keys = []) {
      if (!source || typeof source !== 'object') return []
      for (const key of keys) {
        const val = source[key]
        if (Array.isArray(val)) return val
      }
      return []
    },
    getTransactionRange(trx) {
      const params = (trx && trx.params) ? trx.params : {}
      const fromRaw = params.ch_from != null ? params.ch_from : (trx.ch_from != null ? trx.ch_from : trx.from_chain)
      const toRaw = params.ch_to != null ? params.ch_to : (trx.ch_to != null ? trx.ch_to : trx.to_chain)
      const f = parseFloat(fromRaw != null ? fromRaw : 0)
      const t = parseFloat(toRaw != null ? toRaw : 0)
      return {
        from: Math.min(f, t),
        to: Math.max(f, t),
        rawFrom: fromRaw != null ? fromRaw : 0,
        rawTo: toRaw != null ? toRaw : 0
      }
    },
    normalizeTransaction(parentItem, trxItem) {
      const parent = (parentItem && typeof parentItem === 'object') ? parentItem : {}
      const trx = (trxItem && typeof trxItem === 'object') ? trxItem : {}
      const mergedParams = {
        ...(parent.params || {}),
        ...(trx.params || {})
      }

      if (mergedParams.ch_from == null) mergedParams.ch_from = trx.ch_from != null ? trx.ch_from : parent.ch_from
      if (mergedParams.ch_to == null) mergedParams.ch_to = trx.ch_to != null ? trx.ch_to : parent.ch_to
      if (mergedParams.qty == null) mergedParams.qty = trx.qty != null ? trx.qty : parent.qty

      if (!mergedParams.side) {
        mergedParams.side = trx.side || parent.side || {
          id: trx.side_id || parent.side_id || 'unknown',
          name: trx.side_name || parent.side_name,
          code: trx.side_code || parent.side_code || 'Unknown'
        }
      }

      if (!mergedParams.lane) {
        mergedParams.lane = trx.lane || parent.lane || {
          id: trx.lane_id || parent.lane_id || 'unknown',
          name: trx.lane_name || parent.lane_name,
          code: trx.lane_code || parent.lane_code || 'Unknown'
        }
      }

      const fy = trx.fy || parent.fy || {
        id: trx.financial_year_id || parent.financial_year_id || trx.fy_id || parent.fy_id || 'unknown',
        fy_year: trx.fy_year || parent.fy_year || trx.financial_year || parent.financial_year || 'Unknown',
        fy: trx.fy || parent.fy
      }

      return {
        ...parent,
        ...trx,
        params: mergedParams,
        fy,
        po_number: trx.po_number || parent.po_number || trx.po_no || parent.po_no || 'N/A',
        transaction_date: trx.transaction_date || parent.transaction_date || trx.trx_date || parent.trx_date || null
      }
    },
    onFilterChange() {
      // Logic for filter changes
    },

    captureSelectedFilterLabels() {
      const sideMap = {}
      const laneMap = {}
      const fyMap = {}

      const sideSelect = this.$refs.sideSelect
      const laneSelect = this.$refs.laneSelect
      const financialSelect = this.$refs.financialSelect

      const selectedSides = sideSelect && Array.isArray(sideSelect.value) ? sideSelect.value : []
      selectedSides.forEach(side => {
        if (side && typeof side === 'object') {
          const sideKey = side.id != null ? String(side.id) : String(this.getDisplayLabel(side, ['code', 'description', 'name']))
          const sideLabel = this.getDisplayLabel(side, ['description', 'code', 'name']) || sideKey
          sideMap[sideKey] = sideLabel
        }
      })

      const selectedLanes = laneSelect && Array.isArray(laneSelect.value) ? laneSelect.value : []
      selectedLanes.forEach(lane => {
        if (lane && typeof lane === 'object') {
          const laneKey = lane.id != null ? String(lane.id) : String(this.getDisplayLabel(lane, ['code', 'description', 'name']))
          const laneLabel = this.getDisplayLabel(lane, ['description', 'code', 'name']) || laneKey
          laneMap[laneKey] = laneLabel
        }
      })

      const selectedFinancialYears = financialSelect && Array.isArray(financialSelect.value) ? financialSelect.value : []
      selectedFinancialYears.forEach(fy => {
        if (fy && typeof fy === 'object') {
          const fyKey = fy.id != null ? String(fy.id) : String(this.getDisplayLabel(fy, ['fy_year', 'fy', 'name']))
          const fyLabel = fy.fy_year || fy.fy || this.getDisplayLabel(fy, ['name']) || fyKey
          fyMap[fyKey] = fyLabel
        }
      })

      this.selectedSideLabelMap = sideMap
      this.selectedLaneLabelMap = laneMap
      this.selectedFinancialYearLabelMap = fyMap
    },

    async applyFilters() {
      if (!this.isFilterValid) return

      this.captureSelectedFilterLabels()

      this.loading = true
      this.processedData = []
      this.hasAppliedFilters = false
      this.$vs.loading()
      
      const payload = {
        item_sub_group_ids: this.filters.itemSubGroup,
        side_ids: this.filters.side.join(','),
        lane_ids: this.filters.lane.join(','),
        financial_year_ids: this.filters.financial.join(','),
        from_chain: this.filters.fromChain,
        to_chain: this.filters.toChain,
        difference: this.filters.difference,
        organisation: this.orgId
      }

      try {
        const res = await Request(this, 'GET', this.$store.state.apiendpoints.strip_chart, payload)
        console.log('Strip Chart Response:', res)
        this.rawResponse = res
        this.activeFilters = JSON.parse(JSON.stringify(this.filters))
        this.processResponse(res)
        this.hasAppliedFilters = true
        
        this.$vs.notify({
          title: 'Success',
          text: 'Strip chart generated successfully',
          color: 'success',
          position: 'top-right'
        })
      } catch (err) {
        console.error(err)
        this.$vs.notify({
          title: 'Error',
          text: 'Failed to generate strip chart',
          color: 'danger',
          position: 'top-right'
        })
      } finally {
        this.loading = false
        this.$vs.loading.close()
      }
    },

    processResponse(response) {
      const data = this.extractDataArray(response)
      
      const sidesMap = {}
      const collectiveSides = new Set()
      const collectiveLanes = new Set()
      const collectiveFYs = new Set()
      const laneLabelByKey = {}
      const fyLabelByKey = {}
      const selectedSideKeys = Object.keys(this.selectedSideLabelMap)
      const selectedLaneKeys = (this.activeFilters.lane || this.filters.lane || []).map(id => String(id))
      const selectedFYKeys = (this.activeFilters.financial || this.filters.financial || []).map(id => String(id))
      
      // Process transactions from API response
      if (data.length > 0) {
        data.forEach(item => {
          let transactions = this.getArrayValue(item, ['drp_transaction', 'drp_transactions', 'transactions'])
          if (transactions.length === 0) {
            transactions = [item]
          }
          
          transactions.forEach(trx => {
            const normalizedTrx = this.normalizeTransaction(item, trx)
            // Robust access to params
            const params = normalizedTrx.params || {}
            const side = params.side || { id: 'unknown', code: 'Unknown' }
            const fy = normalizedTrx.fy || { id: 'unknown', fy_year: 'Unknown' }
            const lane = params.lane || { id: 'unknown', code: 'Unknown' }
            const sideKey = String(side.id != null ? side.id : (normalizedTrx.side_id != null ? normalizedTrx.side_id : 'unknown'))

            if (selectedSideKeys.length > 0 && !selectedSideKeys.includes(sideKey)) {
              return
            }

            const sideLabel = this.getDisplayLabel(side, ['description', 'name', 'code']) || this.selectedSideLabelMap[sideKey] || 'Unknown'
            const fyYear = fy.fy_year || 'Unknown'
            const fyKey = String(fy.id != null ? fy.id : fyYear)
            const laneLabel = this.getDisplayLabel(lane, ['description', 'name', 'code']) || 'Unknown'
            const laneKey = lane.id || laneLabel

            collectiveSides.add(sideKey)
            collectiveLanes.add(String(laneKey))
            collectiveFYs.add(String(fyKey))
            laneLabelByKey[String(laneKey)] = laneLabel
            fyLabelByKey[String(fyKey)] = fyYear

            if (!sidesMap[sideKey]) {
              sidesMap[sideKey] = { sideLabel, fys: {} }
            }
            if (!sidesMap[sideKey].fys[fyKey]) {
              sidesMap[sideKey].fys[fyKey] = { fyYear, fyKey, lanes: {} }
            }
            if (!sidesMap[sideKey].fys[fyKey].lanes[laneKey]) {
              sidesMap[sideKey].fys[fyKey].lanes[laneKey] = { laneLabel, laneKey, transactions: [] }
            }
            
            sidesMap[sideKey].fys[fyKey].lanes[laneKey].transactions.push(normalizedTrx)
          })
        })

        // Ensure all lanes appear in all side/fy combinations for consistency
        const allSideKeys = selectedSideKeys.length > 0 ? selectedSideKeys : Array.from(collectiveSides)
        const allLaneKeys = selectedLaneKeys.length > 0 ? selectedLaneKeys : Array.from(collectiveLanes)
        const allFYKeys = selectedFYKeys.length > 0 ? selectedFYKeys : Array.from(collectiveFYs)

        allSideKeys.forEach(sideKey => {
          allFYKeys.forEach(fyKey => {
            const fyYearLabel = fyLabelByKey[String(fyKey)] || this.selectedFinancialYearLabelMap[String(fyKey)] || String(fyKey)
            allLaneKeys.forEach(laneKey => {
              const laneLabel = laneLabelByKey[String(laneKey)] || this.selectedLaneLabelMap[String(laneKey)] || String(laneKey)
              if (!sidesMap[sideKey]) {
                sidesMap[sideKey] = { sideLabel: this.selectedSideLabelMap[String(sideKey)] || String(sideKey), fys: {} }
              }
              if (!sidesMap[sideKey].fys[fyKey]) {
                sidesMap[sideKey].fys[fyKey] = { fyYear: fyYearLabel, fyKey, lanes: {} }
              }
              if (!sidesMap[sideKey].fys[fyKey].lanes[laneKey]) {
                sidesMap[sideKey].fys[fyKey].lanes[laneKey] = { laneLabel, laneKey, transactions: [] }
              }
            })
          })
        })
      }

      // Convert maps to sorted arrays for rendering
      this.processedData = Object.values(sidesMap).map(side => {
        const sortedFys = Object.values(side.fys).map(fy => ({
          ...fy,
          lanes: Object.values(fy.lanes).sort((a, b) => {
            // Sort lanes by their key - handles numeric and string codes
            const aKey = String(a.laneKey || '')
            const bKey = String(b.laneKey || '')
            
            // Extract leading numeric part if exists
            const aMatch = aKey.match(/\d+/)
            const bMatch = bKey.match(/\d+/)
            const aNum = aMatch ? parseInt(aMatch[0], 10) : 0
            const bNum = bMatch ? parseInt(bMatch[0], 10) : 0
            
            // If both have numbers, compare numerically
            if (aNum !== bNum) return aNum - bNum
            
            // Otherwise compare as strings
            return aKey.localeCompare(bKey)
          })
        })).sort((a, b) => a.fyYear.localeCompare(b.fyYear))

        return {
          ...side,
          fys: sortedFys
        }
      }).sort((a, b) => a.sideLabel.localeCompare(b.sideLabel))
    },

    getRowSpan(sideGroup) {
      let span = 0
      sideGroup.fys.forEach(fy => {
        span += fy.lanes.length
      })
      return span
    },

    isCellActive(transactions, start, end) {
      return transactions.some(trx => {
        const range = this.getTransactionRange(trx)
        const trxFrom = range.from
        const trxTo = range.to
        
        // If from and to chainages are equal, treat it as a single point
        if (trxFrom === trxTo) {
          return start <= trxFrom && trxFrom < end
        }
        
        // Otherwise handle range overlap
        return Math.max(start, trxFrom) < Math.min(end, trxTo)
      })
    },

    getCellClass(transactions, start, end) {
      if (this.isCellActive(transactions, start, end)) {
        // Return active color class, can be dynamic based on data
        return 'active-cell'
      }
      return ''
    },

    getCellStyle(fyYear, fyKey, transactions, start, end) {
      if (this.isCellActive(transactions, start, end)) {
        const key = String(fyYear);
        const color = this.yearColorMap[key];
        if (color) {
          return { backgroundColor: color }
        }
      }
      return {}
    },

    getCellTooltip(transactions, start, end) {
      const active = transactions.filter(trx => {
        const range = this.getTransactionRange(trx)
        const trxFrom = range.from
        const trxTo = range.to
        if (trxFrom === trxTo) {
          return start <= trxFrom && trxFrom < end
        }
        return Math.max(start, trxFrom) < Math.min(end, trxTo)
      })
      if (active.length === 0) return ''
      return active.map(a => {
        const poNo = a.po_number || 'N/A'
        const qty = (a.params && a.params.qty) ? a.params.qty : 0
        const range = this.getTransactionRange(a)
        const from = range.rawFrom
        const to = range.rawTo
        const transactionDate = this.formatTransactionDate(a.transaction_date)
      const vendor = a.vendor_name || (a.params && a.params.vendor_name) || a.remark || (a.vendor && (a.vendor.name || a.vendor.description || a.vendor.code)) || 'N/A'
return `PO: ${poNo}\nVendor: ${vendor}\nDate: ${transactionDate}\nRange: ${from} - ${to}\nQty: ${qty}`
      }).join('\n\n')
    },

    resetFilters() {
      this.filters = {
        itemSubGroup: null,
        side: [],
        lane: [],
        financial: [],
        fromChain: '',
        difference: '',
        toChain: ''
      }
      this.activeFilters = {}
      this.selectedSideLabelMap = {}
      this.selectedLaneLabelMap = {}
      this.selectedFinancialYearLabelMap = {}
      this.processedData = []
      this.rawResponse = null
      this.hasAppliedFilters = false
    },

    downloadChart() {
      this.exportExcel();
    },

    exportExcel() {
      if (this.processedData.length === 0) return;

      const title = `Strip Chart - ${this.subGroupLabels.join(', ')}`;
      const points = this.chainagePoints;
      const segments = points.slice(0, -1);
      const yearColorMap = this.yearColorMap;
      
      // Build year-specific styles
      let yearStyles = '';
      let styleIdx = 0;
      const yearToStyleIdx = {};
      Object.keys(yearColorMap).sort().forEach(year => {
        yearToStyleIdx[year] = styleIdx;
        yearStyles += `  <Style ss:ID="ActiveCellFY${styleIdx}">
   <Interior ss:Color="${yearColorMap[year]}" ss:Pattern="Solid"/>
  </Style>
`;
        styleIdx++;
      });

      let xml = `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:html="http://www.w3.org/TR/REC-html40">
 <Styles>
  <Style ss:ID="Default" ss:Name="Normal">
   <Alignment ss:Vertical="Center" ss:Horizontal="Center"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1"/>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1"/>
   </Borders>
   <Font ss:FontName="Calibri" x:Family="Swiss" ss:Size="10"/>
  </Style>
  <Style ss:ID="Side">
   <Font ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="#FF6633" ss:Pattern="Solid"/>
   <Alignment ss:Vertical="Center" ss:Horizontal="Center"/>
  </Style>
  <Style ss:ID="FYLane">
   <Alignment ss:Vertical="Center" ss:Horizontal="Center" ss:WrapText="1"/>
   <Font ss:Size="13" ss:Bold="1"/>
  </Style>
${yearStyles}  <Style ss:ID="Header">
   <Font ss:Bold="1" ss:Color="#FFFFFF" ss:Size="11"/>
   <Interior ss:Color="#444444" ss:Pattern="Solid"/>
   <Alignment ss:Vertical="Center" ss:Horizontal="Center"/>
  </Style>
  <Style ss:ID="InfoValue">
   <Font ss:Bold="1" ss:Size="14"/>
   <Alignment ss:Vertical="Center" ss:Horizontal="Left"/>
  </Style>
  <Style ss:ID="ChainageRow">
   <Font ss:Bold="1"/>
   <Interior ss:Color="#F5F5F5" ss:Pattern="Solid"/>
  </Style>
  <Style ss:ID="ChainagePoint">
   <Font ss:Bold="1"/>
   <Interior ss:Color="#F5F5F5" ss:Pattern="Solid"/>
   <Alignment ss:Horizontal="Left" ss:Vertical="Center"/>
  </Style>
  <Style ss:ID="ChainageEndPoint">
   <Font ss:Bold="1"/>
   <Interior ss:Color="#F5F5F5" ss:Pattern="Solid"/>
   <Alignment ss:Horizontal="Right" ss:Vertical="Center"/>
  </Style>
 </Styles>
 <Worksheet ss:Name="Strip Chart">
  <Table ss:DefaultColumnWidth="40">
   <Column ss:Width="80"/>
   <Column ss:Width="100"/>
   <Row ss:Height="22">
    <Cell ss:StyleID="Header"><Data ss:Type="String">SPV Name</Data></Cell>
    <Cell ss:StyleID="InfoValue"><Data ss:Type="String">${this.spvName}</Data></Cell>
   </Row>
   <Row ss:Height="22">
    <Cell ss:StyleID="Header"><Data ss:Type="String">Item Subgroup</Data></Cell>
    <Cell ss:StyleID="InfoValue"><Data ss:Type="String">${this.subGroupLabels.join(', ')}</Data></Cell>
   </Row>
    <Row ss:Height="15"></Row>
   <Row ss:Height="15"></Row>
   <Row ss:Height="15"></Row>
   <Row ss:Height="25">
    <Cell ss:StyleID="Header"><Data ss:Type="String">SIDE</Data></Cell>
    <Cell ss:StyleID="Header"><Data ss:Type="String">FY / LANE</Data></Cell>
   </Row>`;

      this.processedData.forEach(sideGroup => {
        sideGroup.fys.forEach((fyGroup, fIdx) => {
          const fyYear = String(fyGroup.fyYear);
          const styleIdx = yearToStyleIdx[fyYear];
          const activeCellStyle = `ActiveCellFY${styleIdx}`;
          
          fyGroup.lanes.forEach((laneGroup, lIdx) => {
            const fyLaneLabel = `${fyGroup.fyYear}&#10;${laneGroup.laneLabel}`;
            xml += `<Row ss:Height="38">`;
            
            // Handle Side Column Merge
            if (fIdx === 0 && lIdx === 0) {
              const rowSpan = this.getRowSpan(sideGroup);
              xml += `<Cell ss:MergeDown="${rowSpan - 1}" ss:StyleID="Side"><Data ss:Type="String">${sideGroup.sideLabel}</Data></Cell>`;
              // Next cell (FY/Lane) will be in column 2 automatically
              xml += `<Cell ss:StyleID="FYLane"><Data ss:Type="String" xml:space="preserve">${fyLaneLabel}</Data></Cell>`;
            } else {
              // Skip merged Side column, start at column 2 (FY/Lane)
              xml += `<Cell ss:Index="2" ss:StyleID="FYLane"><Data ss:Type="String" xml:space="preserve">${fyLaneLabel}</Data></Cell>`;
            }

            // Data Cells with year-specific colors
            segments.forEach((p, pIdx) => {
              const active = this.isCellActive(laneGroup.transactions, p, points[pIdx+1]);
              xml += `<Cell ${active ? `ss:StyleID="${activeCellStyle}"` : ''}></Cell>`;
            });

            xml += `</Row>`;
          });
        });

        // Chainage Row
        xml += `<Row ss:StyleID="ChainageRow">
          <Cell ss:Index="2"><Data ss:Type="String">Chainage</Data></Cell>
          ${segments.map(p => `<Cell ss:StyleID="ChainagePoint"><Data ss:Type="String">${p}</Data></Cell>`).join('')}
          <Cell ss:StyleID="ChainageEndPoint"><Data ss:Type="String">${points[points.length-1]}</Data></Cell>
        </Row>`;
      });

      xml += `  </Table>
 </Worksheet>
</Workbook>`;

      const blob = new Blob([xml], { type: 'application/vnd.ms-excel' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `strip_chart_${moment().format('YYYYMMDD_HHmmss')}.xls`;
      a.click();
      window.URL.revokeObjectURL(url);
    }
  }
}
</script>

<style scoped>
.strip-chart-wrapper {
  overflow: auto;
  max-width: 100%;
  border-radius: 8px;
  box-shadow: 0 4px 20px 0 rgba(0,0,0,0.05);
  background: white;
}

.table-responsive {
  max-height: 700px;
}

.strip-chart-table {
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
}

.strip-chart-table th, .strip-chart-table td {
  border: 1px solid #f0f0f0;
  padding: 0;
  text-align: center;
}

.header-row th {
  background: #fcfcfc;
  height: 40px;
  border-bottom: 2px solid #e0e0e0;
}

.sticky-col {
  position: sticky;
  left: 0;
  background: #fff;
  z-index: 10;
  border-right: 2px solid #eee !important;
}

.side-head {
  min-width: 90px;
  color: #626262;
  font-weight: 700;
  text-transform: uppercase;
  font-size: 11px;
}

.side-cell {
  background-color: #fbfbfb;
  vertical-align: middle;
}

.side-badge {
  background: #ff6633; /* Exact Project Orange */
  color: #fff;
  padding: 4px 10px;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 600;
  display: inline-block;
  box-shadow: 0 2px 8px rgba(255, 102, 51, 0.3);
}

.fy-lane-head {
  left: 90px;
  min-width: 130px;
  color: #626262;
  font-weight: 700;
  text-transform: uppercase;
  font-size: 11px;
}

.fy-lane-cell {
  left: 90px;
  background-color: #fff;
  padding: 8px !important;
}

.fy-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.fy-label {
  font-size: 13px;
  color: #999;
  font-weight: 600;
}

.lane-label {
  font-size: 14px;
  font-weight: 700;
  color: #2c3e50;
}

.grid-col-head {
  min-width: 45px;
  background: #f8f8f8;
}

.data-row:hover .grid-cell {
  background-color: #fafafa;
}

.grid-cell {
  min-width: 45px;
  height: 45px;
  transition: all 0.2s ease;
  position: relative;
  border: 1px solid #f0f0f0;
}

.grid-cell:hover {
  background-color: #f0f0f5 !important;
  z-index: 5;
}

.cell-content {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 11px;
}

.active-cell {
  border-top-color: rgba(0,0,0,0.05) !important;
  border-bottom-color: rgba(0,0,0,0.05) !important;
}

.active-cell .cell-content {
  background-color: inherit;
  width: 100%;
  height: 16px; /* Reduced height to look like a bar */
  display: block;
}

/* Ensure no gap between active cells */
.active-cell + .active-cell {
  border-left: none !important;
}

.chainage-row {
  background: #fdfdfd;
}

.chainage-row .empty-cell {
  background-color: #fbfbfb;
}

.chainage-row .title-cell {
  font-weight: 700;
  color: #2c3e50;
  font-size: 13px;
  text-transform: capitalize;
  left: 90px;
  background-color: #fff;
  padding: 8px !important;
}

.chainage-label-cell {
  position: relative;
  border-top: 2px solid #555 !important;
  height: 100px;
}

.chainage-tick {
  width: 2px;
  height: 8px;
  background: #555;
  position: absolute;
  top: 0;
  left: 0;
}

.end-tick {
  left: auto;
  right: 0;
}

.last-point-cell {
  min-width: 0 !important;
  width: 0 !important;
  border-left: none !important;
}

.last-point-cell .chainage-label {
  left: auto;
  right: -12px;
}

.chainage-label {
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  position: absolute;
  top: 12px;
  left: -12px;
  font-size: 11px;
  font-weight: 700;
  color: #444;
  letter-spacing: 0.5px;
}

.first-point-cell .chainage-label {
  left: 2px;
}

.chart-placeholder {
  min-height: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #fcfcfc;
  border: 2px dashed #ebebeb;
  border-radius: 12px;
  margin: 10px;
}

label {
  font-weight: 600;
  color: #5e5e5e;
  display: block;
  margin-bottom: 8px;
  font-size: 13px;
}

.text-danger {
  color: #ea5455;
}

.gap-2 {
  gap: 12px;
}

.mt-4 {
  margin-top: 24px !important;
}

.p-4 {
  padding: 20px !important;
}

.items-center {
  align-items: center !important;
}

.justify-between {
  justify-content: space-between !important;
}

.w-full {
  width: 100% !important;
}

.flex {
  display: flex !important;
}

.mb-3 {
  margin-bottom: 20px !important;
}

.sub-group-header {
  background: #fffafa;
  padding: 10px 15px;
  border-radius: 6px;
  border-left: 4px solid #ff6633;
  display: inline-block;
}

.sub-group-header .label {
  font-weight: 700;
  color: #626262;
  margin-right: 8px;
}

.sub-group-header .value {
  color: #ff6633;
  font-weight: 600;
}

.items-end {
  align-items: flex-end !important;
}

.flex-1 {
  flex: 1 !important;
}
</style>

<style scoped>
.strip-chart-container {
  padding: 20px;
}

.chart-placeholder {
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  border-radius: 8px;
}

label {
  font-weight: 600;
  color: #333;
  display: block;
  margin-bottom: 8px;
}

.mb-2 {
  margin-bottom: 8px !important;
}

.mb-3 {
  margin-bottom: 16px !important;
}

.mb-4 {
  margin-bottom: 24px !important;
}

.mt-4 {
  margin-top: 24px !important;
}

.p-4 {
  padding: 16px !important;
}

.w-full {
  width: 100% !important;
}

.flex {
  display: flex !important;
}

.items-end {
  align-items: flex-end !important;
}

.text-center {
  text-align: center !important;
}

.text-gray-500 {
  color: #999 !important;
}

.gap-2 {
  gap: 8px !important;
}

.flex-1 {
  flex: 1 !important;
}
</style>
